import { ReactComponent as ErrorIcon } from 'assets/alerts/error.svg';
import { ReactComponent as WarningIcon } from 'assets/alerts/warningOutline.svg';
import deleteIcon from 'assets/icon/delete.svg';
import PrintButton from 'components/Buttons/PrintButton/print-button';
import FieldMessage, { MessageType } from 'components/FieldMessage';
import { useContextLayout } from 'components/Layout/context';
import { FilterType } from 'pages/NursingForm/constants';
import React, { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import SVG from 'react-inlinesvg';
import styled from 'styled-components';
import ButtonSubmit from './ButtonSubmit';
import SearchBar, { FilterExtraDropdownProps } from './SearchBar';

type Props = {
  children?: ReactNode;
  id?: string;
  title?: string;
  titleElement?: ReactElement;
  dropDownList?: { url: string; lable: string }[];
  onCancel?: () => void;
  onSubmitFm?: () => void;
  countWarning?: number;
  countError?: number;
  filterDateFrom?: Date;
  filterDateTo?: Date;
  handleDelete?: () => void;
  handleNewForm?: () => void;
  editFormlabel?: string;
  createNewFormLabel?: string;
  addButtonLabel?: string;
  handleEdit?: () => void;
  disabledDelete?: boolean;
  disabledEdit?: boolean;
  disabledAddNew?: boolean;
  disableSubmit?: boolean;
  alwaysShowWarning?: boolean;
  search?: boolean;
  items?: { label: string; value: string }[];
  onFilterChange?: React.Dispatch<React.SetStateAction<FilterType>>;
  isHourFilterable?: boolean;
  editFormVariant?: 'danger' | 'primary' | 'success';
  onFetchInitialForm?: () => void;
  isShowDropDownList?: boolean;
  isShowActive?: boolean;
  saveAndCancelOnly?: boolean;
  hideAddNewButton?: boolean;
  hideEditButton?: boolean;
  showOnlyPrint?: boolean;
  showOnlyTitle?: boolean;
  extraButton?: ReactElement;
  filterExtraDropdown?: FilterExtraDropdownProps;
  addWithModal?: boolean;
  handlePrint?: (() => void) | undefined;
  disablePrint?: boolean;
};

const Title = styled.span`
  margin-right: 10.5px;
  font-weight: normal;
  font-size: 24px;
  line-height: 36px;
  color: ${(props) => props.theme.colors.black};
`;

const SubmitBox = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
  will-change: transform;
  z-index: 100;
`;

const DropDownBox = styled.div<{ showSubmit?: boolean }>`
  display: ${(prop) => (prop.showSubmit ? 'none' : 'block')} !important;
  margin-left: 8px;
`;

export const ConfirmBox = styled.div<{ showSubmit?: boolean }>`
  display: ${(prop) => (prop.showSubmit ? 'flex' : 'none')};
`;

const Warning = styled(WarningIcon)`
  align-self: center;
  margin-left: 6px;
`;

const Error = styled(ErrorIcon)`
  align-self: center;
`;

const CountForm = styled.span<{ type: 'error' | 'warning' }>`
  align-self: center;
  color: ${({ theme, type }) => (type === 'error' ? theme.colors.radicalRed : theme.colors.orange)};
  font-weight: 500;
  font-size: 13px;
  line-height: 19px;
  letter-spacing: -0.01em;
  margin-left: 4px;
`;

// TODO: showSubmit actually means the opposite: when true -> red color (disabled), when false -> normal color (enabled)
export const SwitchBtn = styled.div<{ showSubmit?: boolean }>`
  && {
    margin-left: 8px;
    display: flex;
    border-radius: 8px;
    border: none;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    font-style: normal;
    font-weight: 600;
    font-size: 12px;
    line-height: 18px;
    color: ${(props) => props.theme.colors.white};
    cursor: pointer;
    overflow: hidden;
    background-color: ${(props) =>
      props.showSubmit ? props.theme.colors.radicalRed : props.theme.colors.malibu};
    :hover,
    :active,
    :focus {
      background-color: ${(props) =>
        props.showSubmit ? props.theme.colors.radicalRed : props.theme.colors.malibu} !important;
    }
    transition: width 0.3s;
    width: ${(prop) => (prop.showSubmit ? '84px' : '40px')};
    height: 40px;
  }
`;

export const Button = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  justify-content: center;
  width: 100%;
  height: 40px;
`;

const TitileBox = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
`;

const TitleDate = styled.span`
  margin-left: 6.5px;
  font-style: normal;
  font-weight: 500;
  font-size: 13px;
  line-height: 19px;
  text-decoration-line: underline;
  color: ${(props) => props.theme.colors.ghost};
`;

const ContentWarp = styled.div`
  flex-wrap: wrap;
`;

const ButtonDetete = styled.button`
  background: ${(props) => props.theme.colors.radicalRed};
  align-items: center;
  justify-content: center;
  text-align: center;
  display: flex;
  border-radius: 8px;
  width: 100%;
  height: 100%;
  border: 0;

  &:disabled {
    background: #757677;
  }
`;

const WrapperButton = styled.div`
  display: flex;

  @media (max-width: 450px) {
    gap: 8px;
    > div {
      margin-left: 0px !important;
    }
  }
`;

const WapperIcon = styled.div`
  display: flex;

  @media (max-width: 700px) {
    margin-right: 8px !important;
  }
`;

const FormTitleBar: React.VFC<Props> = ({
  children,
  id,
  title,
  titleElement,
  dropDownList,
  onCancel,
  onSubmitFm,
  countWarning,
  countError,
  filterDateFrom,
  filterDateTo,
  handleDelete,
  handleNewForm,
  editFormlabel,
  createNewFormLabel,
  addButtonLabel,
  handleEdit,
  disabledDelete,
  disabledEdit,
  disabledAddNew = false,
  disableSubmit = false,
  alwaysShowWarning,
  onFilterChange,
  isHourFilterable,
  search,
  items,
  editFormVariant,
  onFetchInitialForm,
  isShowDropDownList,
  isShowActive,
  saveAndCancelOnly = false,
  hideAddNewButton = false,
  hideEditButton = false,
  showOnlyPrint = false,
  showOnlyTitle = false,
  extraButton,
  filterExtraDropdown,
  addWithModal = false,
  handlePrint,
  disablePrint = false,
}) => {
  const { t } = useTranslation();
  const { isEditMode, setIsEditMode } = useContextLayout();
  const handleShowSubmit = () => {
    setIsEditMode(true);
    if (handleEdit) handleEdit();
  };

  const onClickMenu = (event: any) => {
    if (addWithModal || event?.target.text === 'leeres Formular') {
      handleNewForm && handleNewForm();
    } else if (event?.target.text === 'aktuelle Form') {
      setIsEditMode(true);
      onCancel && onCancel();
      onFetchInitialForm && onFetchInitialForm();
    } else {
      return;
    }
  };

  return (
    <ContentWarp className="d-flex justify-content-between">
      <TitileBox>
        {titleElement ? titleElement : <Title>{title}</Title>}
        {isShowActive && (
          <FieldMessage id="warningInformation" type={MessageType.INFO}>
            {t('discontinue')}
          </FieldMessage>
        )}
      </TitileBox>

      {showOnlyTitle ? (
        ''
      ) : search ? (
        <SearchBar
          items={items}
          disablePrint={!!disablePrint}
          handlePrint={handlePrint}
          onClick={handleShowSubmit}
          onFilterChange={onFilterChange}
          isHourFilterable={isHourFilterable}
          addButtonLabel={addButtonLabel}
          filterExtraDropdown={filterExtraDropdown}
          disableClickAway={!!filterExtraDropdown}
          filterDateFrom={filterDateFrom}
          filterDateTo={filterDateTo}
          disableAddNew={disabledAddNew}
        />
      ) : children ? (
        children
      ) : (
        <SubmitBox className="mt-sm-0 mt-4">
          <ConfirmBox showSubmit={isEditMode || alwaysShowWarning}>
            <WapperIcon>
              {countError && countError > 0 ? (
                <>
                  <Error />
                  <CountForm type="error">{countError}</CountForm>
                </>
              ) : undefined}
              {countWarning && countWarning > 0 ? (
                <>
                  <Warning />
                  <CountForm type="warning">{countWarning}</CountForm>
                </>
              ) : undefined}
            </WapperIcon>
          </ConfirmBox>
          <ConfirmBox showSubmit={isEditMode}>
            <ButtonSubmit
              lable={t('submit')}
              onClick={onSubmitFm}
              disabled={disableSubmit}
              data-test="resident-submit-button"
            />
          </ConfirmBox>
          {!saveAndCancelOnly && !showOnlyPrint && (
            <DropDownBox showSubmit={isEditMode}>
              <ButtonSubmit
                type={dropDownList ? 'dropdown' : 'button'}
                lable={editFormlabel || t('toEdit')}
                disabled={disabledEdit}
                disableAddNewButton={disabledAddNew}
                createNewFormLabel={createNewFormLabel || t('toNew')}
                dropDownList={dropDownList}
                onClick={() => handleShowSubmit()}
                variant={editFormVariant}
                onCreateNewForm={() => {
                  !addWithModal && setIsEditMode(true);
                  handleNewForm && handleNewForm();
                }}
                onClickMenu={onClickMenu}
                isShowDropDownList={isShowDropDownList}
                hideAddNewButton={hideAddNewButton}
                hideEditButton={hideEditButton}
              />
            </DropDownBox>
          )}
          <WrapperButton>
            {isEditMode && (
              <SwitchBtn showSubmit={isEditMode}>
                <Button
                  onClick={() => {
                    onCancel && onCancel();
                    setIsEditMode(false);
                  }}
                >
                  {t('cancel')}
                </Button>
              </SwitchBtn>
            )}

            {!isEditMode && extraButton}

            {!isEditMode && !saveAndCancelOnly && !disablePrint && (
              <PrintButton disabled={disablePrint} handlePrint={handlePrint} />
            )}

            {handleDelete && !isEditMode && (
              <SwitchBtn>
                <ButtonDetete
                  id={`${id}-title-delete-button`}
                  disabled={disabledDelete}
                  type="button"
                  onClick={() => {
                    handleDelete();
                  }}
                >
                  <SVG src={deleteIcon} width={21} />
                </ButtonDetete>
              </SwitchBtn>
            )}
          </WrapperButton>
        </SubmitBox>
      )}
    </ContentWarp>
  );
};
export default FormTitleBar;
